import React, { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import {
  faClock,
  faEdit,
  faPlus,
  faTrash,
  faTimes,
  faTimesCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { Button, Popover, UncontrolledPopover, PopoverHeader, PopoverBody, UncontrolledTooltip } from 'reactstrap';
import AddTimeSlotPopover from '../AddTimeSlotPopover';
import CustomEventPopover from '../CustomEventPopover';
import CustomDurationPopover from '../CustomDurationPopover';

import style from './style';
import Swal from 'sweetalert2';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { deleteData } from '../../../../../Services/dataApi';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { popoverModifiers } from 'enums';

const TimeSlotPopover = ({
  forcedContext,
  row,
  addRowCustomEvent,
  hideHeaderPopover,
  scheduleData,
  fetchSchedule,
  pk_InterviewDate,
  visibility = {},
  setTimeSlotChildVisibility,
  fireConfirmationForEditingPastSchedule,
  dateIsInThePast,
  willEditThePast,
  showPastScheduleWarning,
  onScheduleTimeShift,
}) => {
  const [customEventHasChanges, setCustomEventHasChanges] = useState(false);
  const [addTimeSlotHasChanges, setAddTimeSlotHasChanges] = useState(false);
  const containerRef = useRef(null);
  const addTimeSlotPopoverRef = useRef(null);
  const addCustomPopoverRef = useRef(null);
  const customDurationPopoverRef = useRef(null);
  const deptContext = useContext(DepartmentContext);
  const dContext = forcedContext ? forcedContext : deptContext;

  const { department = {} } = dContext;
  const { VirtualMeetingMode } = department;

  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const showAddTimeSlotPopover = Boolean(visibility.showAddTimeSlotPopover);
  const showAddCustomPopover = Boolean(visibility.showAddCustomPopover);
  const showCustomDurationPopover = Boolean(visibility.showCustomDurationPopover);
  const anyChildOpen = showAddTimeSlotPopover || showAddCustomPopover || showCustomDurationPopover;
  const getTargetSummary = (target) => {
    if (!target) {
      return 'no-target';
    }

    const id = target.id ? `#${target.id}` : '';
    const className = target.className
      ? `.${`${target.className}`.split(' ').filter(Boolean).join('.')}`
      : '';

    return `${target.tagName || 'unknown'}${id}${className}`;
  };

  const logPopoverDebug = (event, payload = {}) => {
    console.log(
      JSON.stringify({
        scope: 'SchedulePopover',
        source: 'TimeSlotPopover',
        event,
        pk_Timeslot: row.pk_Timeslot,
        ...payload,
      }),
    );
  };

  const isWithinTarget = (target, id) => {
    if (typeof document === 'undefined') {
      return false;
    }

    const element = document.getElementById(id);
    return Boolean(element && element.contains(target));
  };

  const closeAddTimeSlotPopover = async () => {
    logPopoverDebug('closeAddTimeSlotPopover:start', {
      showAddTimeSlotPopover,
      addTimeSlotHasChanges,
    });

    if (!showAddTimeSlotPopover) {
      return true;
    }

    if (addTimeSlotHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        return false;
      }
      setAddTimeSlotHasChanges(false);
    }

    setTimeSlotChildVisibility('showAddTimeSlotPopover', false);
    logPopoverDebug('closeAddTimeSlotPopover:closed');
    return true;
  };

  const openAddTimeSlotPopover = async () => {
    logPopoverDebug('openAddTimeSlotPopover:start', {
      dateIsInThePast,
      willEditThePast,
      customEventHasChanges,
    });

    if (dateIsInThePast && !willEditThePast) {
      const continueProcess = await fireConfirmationForEditingPastSchedule();
      if (!continueProcess) {
        logPopoverDebug('openAddTimeSlotPopover:blockedPastWarning');
        return false;
      }
    }

    if (customEventHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes in Custom Event. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        logPopoverDebug('openAddTimeSlotPopover:keptCustomEventEditing');
        return false;
      }
      setCustomEventHasChanges(false);
    }

    setTimeSlotChildVisibility('showAddTimeSlotPopover', true);
    logPopoverDebug('openAddTimeSlotPopover:opened');
    return true;
  };

  const toggleAddTimeSlotPopover = async (e) => {
    logPopoverDebug('toggleAddTimeSlotPopover:click', {
      showAddTimeSlotPopover,
      target: getTargetSummary(e?.target),
    });
    e?.stopPropagation?.();

    if (showAddTimeSlotPopover) {
      await closeAddTimeSlotPopover();
      return;
    }

    await openAddTimeSlotPopover();
  };

  const closeAddCustomPopover = async () => {
    logPopoverDebug('closeAddCustomPopover:start', {
      showAddCustomPopover,
      customEventHasChanges,
    });

    if (!showAddCustomPopover) {
      return true;
    }

    if (customEventHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        return false;
      }
      setCustomEventHasChanges(false);
    }

    setTimeSlotChildVisibility('showAddCustomPopover', false);
    logPopoverDebug('closeAddCustomPopover:closed');
    return true;
  };

  const openAddCustomPopover = async () => {
    logPopoverDebug('openAddCustomPopover:start', {
      showPastScheduleWarning,
      addTimeSlotHasChanges,
    });

    if (showPastScheduleWarning) {
      logPopoverDebug('openAddCustomPopover:blockedPastScheduleWarning');
      return false;
    }

    if (addTimeSlotHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes in Insert Time Slots. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        logPopoverDebug('openAddCustomPopover:keptAddTimeSlotEditing');
        return false;
      }
      setAddTimeSlotHasChanges(false);
    }

    setTimeSlotChildVisibility('showAddCustomPopover', true);
    logPopoverDebug('openAddCustomPopover:opened');
    return true;
  };

  const toggleAddCustomPopover = async (e) => {
    logPopoverDebug('toggleAddCustomPopover:click', {
      showAddCustomPopover,
      target: getTargetSummary(e?.target),
    });
    e?.stopPropagation?.();

    if (showAddCustomPopover) {
      await closeAddCustomPopover();
      return;
    }

    await openAddCustomPopover();
  };

  const closeCustomDurationPopover = async () => {
    logPopoverDebug('closeCustomDurationPopover:start', {
      showCustomDurationPopover,
    });

    if (!showCustomDurationPopover) {
      return true;
    }

    setTimeSlotChildVisibility('showCustomDurationPopover', false);
    logPopoverDebug('closeCustomDurationPopover:closed');
    return true;
  };

  const openCustomDurationPopover = async () => {
    logPopoverDebug('openCustomDurationPopover:start', {
      showPastScheduleWarning,
      customEventHasChanges,
      addTimeSlotHasChanges,
    });

    if (showPastScheduleWarning) {
      logPopoverDebug('openCustomDurationPopover:blockedPastScheduleWarning');
      return false;
    }

    if (customEventHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes in Custom Event. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        logPopoverDebug('openCustomDurationPopover:keptCustomEventEditing');
        return false;
      }
      setCustomEventHasChanges(false);
    }

    if (addTimeSlotHasChanges) {
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes in Insert Time Slots. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (!result.isConfirmed) {
        logPopoverDebug('openCustomDurationPopover:keptAddTimeSlotEditing');
        return false;
      }
      setAddTimeSlotHasChanges(false);
    }

    setTimeSlotChildVisibility('showCustomDurationPopover', true);
    logPopoverDebug('openCustomDurationPopover:opened');
    return true;
  };

  const toggleCustomDurationPopover = async (e) => {
    logPopoverDebug('toggleCustomDurationPopover:click', {
      showCustomDurationPopover,
      target: getTargetSummary(e?.target),
    });
    e?.stopPropagation?.();

    if (showCustomDurationPopover) {
      await closeCustomDurationPopover();
      return;
    }

    await openCustomDurationPopover();
  };

  useEffect(() => {
    const handleDocumentMouseDown = (e) => {
      const target = e.target;
      const clickedInsideParentContent = Boolean(containerRef.current && containerRef.current.contains(target));
      const clickedParentTarget = isWithinTarget(target, `timeSlot_${row.pk_Timeslot}`);
      const clickedChildTarget =
        isWithinTarget(target, `addSlot_${row.pk_Timeslot}`) ||
        isWithinTarget(target, `addCustomEvent_${row.pk_Timeslot}`) ||
        isWithinTarget(target, `customDuration_${row.pk_Timeslot}`);
      const clickedChildPopover =
        Boolean(addTimeSlotPopoverRef.current && addTimeSlotPopoverRef.current.contains(target)) ||
        Boolean(addCustomPopoverRef.current && addCustomPopoverRef.current.contains(target)) ||
        Boolean(customDurationPopoverRef.current && customDurationPopoverRef.current.contains(target));

      logPopoverDebug('rowDocumentMouseDown', {
        target: getTargetSummary(target),
        clickedInsideParentContent,
        clickedParentTarget,
        clickedChildTarget,
        clickedChildPopover,
        showAddTimeSlotPopover,
        showAddCustomPopover,
        showCustomDurationPopover,
      });

      if (clickedChildTarget || clickedChildPopover) {
        return;
      }

      if (anyChildOpen) {
        if (showAddTimeSlotPopover) {
          void closeAddTimeSlotPopover();
          return;
        }

        if (showAddCustomPopover) {
          void closeAddCustomPopover();
          return;
        }

        if (showCustomDurationPopover) {
          void closeCustomDurationPopover();
          return;
        }
      }

      if (!clickedInsideParentContent && !clickedParentTarget) {
        hideHeaderPopover();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [
    anyChildOpen,
    hideHeaderPopover,
    row.pk_Timeslot,
    showAddCustomPopover,
    showAddTimeSlotPopover,
    showCustomDurationPopover,
  ]);

  useEffect(() => {
    logPopoverDebug('rowVisibilityChanged', {
      self: Boolean(visibility.self),
      showAddTimeSlotPopover,
      showAddCustomPopover,
      showCustomDurationPopover,
    });
  }, [
    row.pk_Timeslot,
    showAddCustomPopover,
    showAddTimeSlotPopover,
    showCustomDurationPopover,
    visibility.self,
  ]);

  const deleteRow = () => {
    getTokenSilently()
      .then((token) => {
        deleteData(
          '/department/season/schedule/row',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Timeslot: row.pk_Timeslot,
            pk_InterviewDate: pk_InterviewDate,
          },
          null,
          formatBearerToken(token),
        )
          .then(async (res) => {
            const removedMinutes = Math.max(1, dayjs(row.slotEnd).diff(dayjs(row.slot), 'minute'));
            if (onScheduleTimeShift && removedMinutes > 0 && row.slot) {
              await onScheduleTimeShift({
                deltaMinutes: -removedMinutes,
                anchorDateTime: row.slot,
              });
            }

            fetchSchedule(token);
            hideHeaderPopover();
          })
          .catch((err) => {
            hideHeaderPopover();
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const renderAddTimeSlotPopOver = () => {
    return (
      <Popover
        placementPrefix="addTimeSlotPopover bs-popover" // Change with room id later
        target={`addSlot_${row.pk_Timeslot}`} // Change with room id later
        trigger="manual"
        innerRef={(node) => {
          addTimeSlotPopoverRef.current = node;
        }}
        isOpen={showAddTimeSlotPopover}
        toggle={() => {}}
      >
        <PopoverHeader>
          <div style={style.flexEndRow}>Add time slot/s to schedule</div>
        </PopoverHeader>
        <PopoverBody
          style={style.popOverBody}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <AddTimeSlotPopover
            forcedContext={forcedContext}
            pk_InterviewDate={pk_InterviewDate}
            row={row}
            fetchSchedule={fetchSchedule}
            scheduleData={scheduleData}
            hideHeaderPopover={hideHeaderPopover}
            setShowAddTimeSlotPopover={(isOpen) => setTimeSlotChildVisibility('showAddTimeSlotPopover', isOpen)}
            isOpen={showAddTimeSlotPopover}
            onChangeDetected={setAddTimeSlotHasChanges}
            onScheduleTimeShift={onScheduleTimeShift}
          />
        </PopoverBody>
      </Popover>
    );
  };

  const renderAddCustomEventPopOver = () => {
    const isMainRoomAndNoLink =
      row.CustomMeetingTitle && row.VirtualRoomType === 'AllDay' && !scheduleData.metaData.MainRoomURL;
    const isCustomAndNoLink = row.CustomMeetingTitle && row.VirtualRoomType === 'Custom' && !row.CustomMeetingUrl;
    const urlIssue = VirtualMeetingMode !== 'Disabled' && (isMainRoomAndNoLink || isCustomAndNoLink);

    return (
      <Popover
        placementPrefix="customEvent bs-popover" // Change with room id later
        // popperClassName="customEvent" // Change with room id later
        target={`addCustomEvent_${row.pk_Timeslot}`} // Change with room id later
        trigger="manual"
        modifiers={popoverModifiers}
        innerRef={(node) => {
          addCustomPopoverRef.current = node;
        }}
        isOpen={showAddCustomPopover}
        toggle={() => {}}
      >
        <PopoverHeader>
          <div style={style.spaceBetweenRow}>
            <div style={style.headerTitle}>Custom Event</div>
            <FontAwesomeIcon
              icon={faTimesCircle}
              style={style.buttonIcon}
              onClick={async (e) => {
                e.stopPropagation();

                // Check for unsaved changes before closing
                if (customEventHasChanges) {
                  const result = await Swal.fire({
                    title: 'Unsaved Changes',
                    text: 'You have unsaved changes. Do you want to discard them?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                  });
                  if (!result.isConfirmed) {
                    return; // Don't close the popover
                  }
                  setCustomEventHasChanges(false);
                }

                await closeAddCustomPopover();
              }}
            />
          </div>
        </PopoverHeader>
        <PopoverBody style={style.popOverBody}>
          <CustomEventPopover
            row={row}
            setShowAddCustomPopover={(isOpen) => setTimeSlotChildVisibility('showAddCustomPopover', isOpen)}
            addRowCustomEvent={addRowCustomEvent}
            hideHeaderPopover={hideHeaderPopover}
            scheduleData={scheduleData}
            fireConfirmationForEditingPastSchedule={fireConfirmationForEditingPastSchedule}
            dateIsInThePast={dateIsInThePast}
            willEditThePast={willEditThePast}
            isOpen={showAddCustomPopover}
            onChangeDetected={setCustomEventHasChanges}
          />
        </PopoverBody>
      </Popover>
    );
  };

  const renderCustomDurationPopover = () => {
    return (
      <Popover
        placementPrefix="customDuration bs-popove" // Change with room id later
        target={`customDuration_${row.pk_Timeslot}`} // Change with room id later
        trigger="manual"
        modifiers={popoverModifiers}
        innerRef={(node) => {
          customDurationPopoverRef.current = node;
        }}
        isOpen={showCustomDurationPopover}
        toggle={() => {}}
      >
        <PopoverHeader>
          <div style={style.spaceBetweenRow}>
            <div style={style.headerTitle}>Custom Duration</div>
            <FontAwesomeIcon
              icon={faTimesCircle}
              style={style.buttonIcon}
              onClick={(e) => {
                e.stopPropagation();
                void closeCustomDurationPopover();
              }}
            />
          </div>
        </PopoverHeader>
        <PopoverBody style={style.popOverBody}>
          <CustomDurationPopover
            row={row}
            scheduleData={scheduleData}
            setShowCustomDurationPopover={(isOpen) =>
              setTimeSlotChildVisibility('showCustomDurationPopover', isOpen)
            }
            addRowCustomEvent={addRowCustomEvent}
            hideHeaderPopover={hideHeaderPopover}
            fireConfirmationForEditingPastSchedule={fireConfirmationForEditingPastSchedule}
            dateIsInThePast={dateIsInThePast}
            willEditThePast={willEditThePast}
            showPastScheduleWarning={showPastScheduleWarning}
            isOpen={showCustomDurationPopover}
            onScheduleTimeShift={onScheduleTimeShift}
          />
        </PopoverBody>
      </Popover>
    );
  };

  const isMainRoomAndNoLink =
    row.CustomMeetingTitle && row.VirtualRoomType === 'AllDay' && !scheduleData.metaData.MainRoomURL;
  const isCustomAndNoLink = row.CustomMeetingTitle && row.VirtualRoomType === 'Custom' && !row.CustomMeetingUrl;
  const urlIssue = VirtualMeetingMode !== 'Disabled' && (isMainRoomAndNoLink || isCustomAndNoLink);

  return (
    <div ref={containerRef}>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          color="success"
          id={`addSlot_${row.pk_Timeslot}`}
          style={{ ...style.genButton }}
          onClick={toggleAddTimeSlotPopover}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faPlus} style={style.buttonIcon} />
            <div style={style.buttonLabel}>Insert Time Slots (Rows)</div>
          </div>
        </Button>
      </div>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          id={`addCustomEvent_${row.pk_Timeslot}`}
          style={{ ...style.genButton, backgroundColor: 'var(--rr-primary-strong)', color: 'var(--rr-on-primary)' }}
          onClick={toggleAddCustomPopover}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faEdit} style={style.buttonIcon} />
            <div style={style.buttonLabel}>
              Custom Event{' '}
              {urlIssue ? (
                <>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ marginLeft: 5 }}
                    id={`url_warning_customEventBtn_${row.pk_Timeslot}`}
                  />
                  <UncontrolledTooltip target={`url_warning_customEventBtn_${row.pk_Timeslot}`}>
                    Warning: {isCustomAndNoLink ? `Custom Event URL is missing!` : `Main Room URL is missing!`}
                  </UncontrolledTooltip>
                </>
              ) : null}
            </div>
          </div>
        </Button>
      </div>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          id={`customDuration_${row.pk_Timeslot}`}
          color="warning"
          style={{ ...style.genButton }}
          onClick={toggleCustomDurationPopover}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faClock} style={style.buttonIcon} />
            <div style={style.buttonLabel}>
              Custom Duration{' '}
              {row.StandardPassingDurationInMinutes == 0 || row.CustomPassingDurationInMinutes == 0 ? (
                <>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ color: 'black', marginLeft: 5 }}
                    id={`passing_period_warning_customDurationBtn_${row.pk_Timeslot}`}
                  />
                  <UncontrolledTooltip target={`passing_period_warning_customDurationBtn_${row.pk_Timeslot}`}>
                    Warning: No passing period - users will be unable to join meetings prior to the start time.
                  </UncontrolledTooltip>
                </>
              ) : null}
            </div>
          </div>
        </Button>
      </div>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          color="danger"
          style={{ ...style.genButton }}
          onClick={async (e) => {
            e.stopPropagation();
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            Swal.fire({
              title: 'Are you sure?',
              text: "You won't be able to revert this!",
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: 'Yes, delete it!',
            }).then((result) => {
              if (result.isConfirmed) {
                deleteRow();
                // delete row here
              } else {
              }
            });
          }}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faTrash} style={style.buttonIcon} />
            <div style={style.buttonLabel}>Delete Time Slot (Row)</div>
          </div>
        </Button>
      </div>
      {renderAddTimeSlotPopOver()}
      {renderAddCustomEventPopOver()}
      {renderCustomDurationPopover()}
    </div>
  );
};

export default TimeSlotPopover;
